/**
 * Destaque das áreas do filtro dos "Últimos focos" no mapa:
 * - o resto do mapa fica escurecido (retângulo escuro no canvas da camada,
 *   com as áreas "recortadas" por `destination-out` — áreas sobrepostas, como
 *   um município dentro do estado, continuam claras);
 * - as áreas ganham contorno laranja com brilho.
 *
 * Fica acima das camadas vetoriais e abaixo dos focos (Z.AREA_FOCUS). Classe
 * própria: o canvas não é compartilhado (o recorte apagaria outras camadas).
 * Poucas feições: redesenhar durante arrastos e animações é barato, e evita
 * bordas sem escurecimento ao mover o mapa. A exportação captura o canvas.
 */
import Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import VectorLayer from 'ol/layer/Vector';
import type RenderEvent from 'ol/render/Event';
import VectorSource from 'ol/source/Vector';
import { Stroke, Style } from 'ol/style';
import type { Coordinate } from 'ol/coordinate';
import { Z } from '../map/zIndex';
import { getAreaGeometries, onAreaGeometries } from './areaSync';

/** Escurecimento fora das áreas. */
export const AREA_DIM = 'rgba(7, 11, 20, 0.5)';
const OUTLINE = '#fb923c';

function tracePolygon(ctx: CanvasRenderingContext2D, rings: Coordinate[][]): void {
  for (const ring of rings) {
    if (!ring.length) continue;
    ctx.moveTo(ring[0]![0]!, ring[0]![1]!);
    for (let i = 1; i < ring.length; i++) ctx.lineTo(ring[i]![0]!, ring[i]![1]!);
    ctx.closePath();
  }
}

/** Recorta a área do escurecimento (zIndex 0: antes de todos os contornos). */
const cutStyle = new Style({
  zIndex: 0,
  renderer: (coordinates, state) => {
    const ctx = state.context;
    const type = state.geometry.getType();
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = '#000';
    ctx.beginPath();
    if (type === 'Polygon') tracePolygon(ctx, coordinates as Coordinate[][]);
    else if (type === 'MultiPolygon') for (const poly of coordinates as Coordinate[][][]) tracePolygon(ctx, poly);
    ctx.fill('evenodd');
    ctx.restore();
  },
});

export function areaFocusStyles(): Style[] {
  return [
    cutStyle,
    new Style({ zIndex: 1, stroke: new Stroke({ color: 'rgba(251, 146, 60, 0.28)', width: 9, lineJoin: 'round' }) }),
    new Style({ zIndex: 1, stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.55)', width: 3.5, lineJoin: 'round' }) }),
    new Style({ zIndex: 2, stroke: new Stroke({ color: OUTLINE, width: 2, lineJoin: 'round' }) }),
  ];
}

export function createAreaFocusLayer(): { layer: VectorLayer<VectorSource>; dispose: () => void } {
  const source = new VectorSource<Feature<Geometry>>({ wrapX: false });
  const layer = new VectorLayer({
    source,
    style: areaFocusStyles(),
    className: 'ol-layer amaview-area-focus',
    zIndex: Z.AREA_FOCUS,
    updateWhileAnimating: true,
    updateWhileInteracting: true,
    visible: false,
    properties: { id: 'area-focus', title: 'Áreas do filtro' },
  });
  layer.setZIndex(Z.AREA_FOCUS);
  // Escurece todo o canvas antes das feições (que recortam as áreas).
  layer.on('prerender', (e: RenderEvent) => {
    const ctx = e.context as CanvasRenderingContext2D | undefined;
    if (!ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = AREA_DIM;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
  });

  const sync = (list: readonly Geometry[]) => {
    source.clear(true);
    source.addFeatures(list.map((g) => new Feature(g)));
    layer.setVisible(list.length > 0);
  };
  sync(getAreaGeometries());
  const unsubscribe = onAreaGeometries(sync);
  return {
    layer,
    dispose: () => {
      unsubscribe();
      layer.dispose();
    },
  };
}
